// components/common/DraggableDialog.js
import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Dialog, DialogTitle, DialogContent, IconButton, Paper, useTheme, useMediaQuery } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import RefreshIcon from "@mui/icons-material/Refresh";
import Draggable from 'react-draggable';
import { Box } from '@mui/system';
import ConfirmDiscardDialog from '../ConfirmDiscardDialog';

// Helper functions for position persistence
const getPopupPosition = (popupId) => {
  try {
    const savedPosition = localStorage.getItem(`popup_position_${popupId}`);
    return savedPosition ? JSON.parse(savedPosition) : { x: 0, y: 0 };
  } catch (error) {
    console.warn('Failed to load popup position:', error);
    return { x: 0, y: 0 };
  }
};

const savePopupPosition = (popupId, position) => {
  try {
    localStorage.setItem(`popup_position_${popupId}`, JSON.stringify(position));
  } catch (error) {
    console.warn('Failed to save popup position:', error);
  }
};

// Draggable paper component
const DraggablePaper = ({ popupId, onDragStop, defaultPosition, ...props }) => (
  <Draggable
    handle="#draggable-dialog-title"
    cancel={'[class*="MuiDialogContent-root"], .resize-handle'}
    defaultPosition={defaultPosition}
    onStop={onDragStop}
  >
    <Paper {...props} />
  </Draggable>
);

const DraggableDialog = ({
  onClose,
  onRefresh,
  title,
  children,
  maxWidth = 'sm',
  fullWidth = true,
  refreshBtn = false,
  isDataGrid = false,
  disableBackdropClose = true,
  popupId = 'default',
  hasUnsavedChanges = false,
  showUnsavedConfirmCancel = false,
  onUnsavedConfirmCancel = () => {}
}) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [showConfirm, setShowConfirm] = useState(false);

  // Position persistence
  const [position, setPosition] = useState(() => getPopupPosition(popupId));

  const handleDragStop = useCallback((e, data) => {
    const newPosition = { x: data.x, y: data.y };
    setPosition(newPosition);
    savePopupPosition(popupId, newPosition);
  }, [popupId]);

  // Choose the appropriate PaperComponent
  let paperComponent;
  let paperProps = {};

  if (isFullscreen || isMobile) {
    paperComponent = undefined; // No dragging on fullscreen or mobile
  } else {
    paperComponent = DraggablePaper;
    paperProps = {
      popupId,
      onDragStop: handleDragStop,
      defaultPosition: position
    };
  }

  const requestClose = (event, reason) => {
    if (hasUnsavedChanges) {
      setShowConfirm(true);
      return;
    }
    onClose(event, reason);
  };

  return (
    <>
      <Dialog
        open
        onClose={(event, reason) => {
          // Only block backdrop clicks when disableBackdropClose is true
          if (disableBackdropClose && reason === "backdropClick") {
            return;
          }
          requestClose(event, reason);
        }}
        disableEscapeKeyDown={disableBackdropClose}
        PaperComponent={paperComponent}
        PaperProps={paperProps}
        maxWidth={maxWidth}
        fullWidth={fullWidth}
        fullScreen={isFullscreen}
        hideBackdrop={disableBackdropClose}
        disableAutoFocus={true}
        disableEnforceFocus={true}
        disableRestoreFocus={false}
        sx={{
          ...(disableBackdropClose && {
            pointerEvents: 'none',
            '& .MuiDialog-paper': {
              pointerEvents: 'auto',
            }
          })
        }}
      >
        <DialogTitle
          id="draggable-dialog-title"
          style={{ cursor: isFullscreen ? "default" : 'move', position: 'relative', paddingRight: 40, display: 'flex', alignItems: 'center' }}
        >
          {title}
          {refreshBtn && (
            <IconButton
              onClick={onRefresh}
              title="Refresh"
              style={{ marginLeft: 12 }}
            >
              <RefreshIcon />
            </IconButton>
          )}
          <IconButton
            onClick={() => setIsFullscreen(!isFullscreen)}
            title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
            style={{ position: 'absolute', top: 12, right: 65 }}
          >
            {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
          </IconButton>
          <IconButton
            onClick={(event) => requestClose(event, 'closeButtonClick')}
            style={{ position: 'absolute', right: 10, top: 12 }}
            aria-label="close"
            title="Close"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{ padding: 0 }}
          dividers
        >
          <Box
            sx={{
              maxWidth: isDataGrid ? undefined : 900,
              margin: '0 auto',
              width: '100%',
              height: 'auto', // Always auto height for proper scrolling
            }}
          >
            {children}
          </Box>
        </DialogContent>
      </Dialog>

      {/* Confirmation Dialog */}
      <ConfirmDiscardDialog
        open={showConfirm || showUnsavedConfirmCancel}
        onCancel={() => {
          setShowConfirm(false);
          onUnsavedConfirmCancel();
        }}
        onConfirm={() => {
          setShowConfirm(false);
          onClose(undefined, 'discardConfirmed');
        }}
      />
    </>
  );
};

DraggableDialog.propTypes = {
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  maxWidth: PropTypes.string,
  fullWidth: PropTypes.bool,
  refreshBtn: PropTypes.bool,
  isDataGrid: PropTypes.bool,
  disableBackdropClose: PropTypes.bool,
  onRefresh: PropTypes.func,
  popupId: PropTypes.string,
  hasUnsavedChanges: PropTypes.bool,
  showUnsavedConfirmCancel: PropTypes.bool,
  onUnsavedConfirmCancel: PropTypes.func
};

export default DraggableDialog;
